<template>
  <div class="bg-gray-50/50 p-4 sm:p-6 md:p-8">
    <!-- Header -->
    <div class="max-w-7xl mx-auto mb-12 text-center">
      <h1 class="text-4xl font-bold text-gray-800">Choose Your Plan</h1>
      <p class="mt-2 text-lg text-gray-600">
        Select the products that best fit your organization's needs.
      </p>
    </div>

    <!-- Main Content: Product Cards -->
    <div class="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
  <div
    v-for="product in products"
    :key="product.id"
    :class="[
      'bg-white rounded-2xl p-6 flex flex-col transition-all duration-300 shadow-md hover:shadow-xl',
      isInCart(product.id) ? 'ring-2 ring-orange-500' : 'border border-gray-200'
    ]"
  >
    <!-- Product Header: Changed items-center to items-start and added text-left -->
    <div class="flex items-start mb-4 text-left">
      <div class="bg-orange-100 p-2 rounded-lg mr-4 mt-1"> <!-- mt-1 helps align icon with first line of text -->
        <svg class="h-8 w-8 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" v-html="product.icon"></svg>
      </div>
      <div>
        <h2 class="text-xl font-bold text-gray-800 leading-tight">{{ product.name }}</h2>
        <p class="text-xs text-gray-500 uppercase tracking-wide">{{ product.subtitle }}</p>
      </div>
    </div>

    <!-- Description and Features: Added text-left -->
    <div class="flex-grow mb-6 text-left">
      <p class="text-gray-600 mb-5 text-sm">{{ product.description }}</p>
      <ul class="space-y-3">
        <li v-for="feature in product.features" :key="feature" class="flex items-start text-gray-700">
          <svg class="h-5 w-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          <span class="text-sm">{{ feature }}</span>
        </li>
      </ul>
    </div>

        <!-- User Slider and Price (Only shown if in cart) -->
        <div v-if="isInCart(product.id)" class="mt-auto pt-4 border-t border-gray-200">
           <label class="font-semibold text-gray-700">Number Of Users:</label>
           <div class="flex items-center justify-between mt-2">
             <input
               type="range"
               min="10"
               max="250"
               step="10"
               v-model.number="cart[product.id].userCount"
               class="w-full h-2 appearance-none cursor-pointer slider-custom"
             />
             <span class="ml-4 font-bold text-lg text-orange-600 w-16 text-center">{{ cart[product.id].userCount }}</span>
           </div>
        </div>
        
        <!-- Action Button -->
        <div class="mt-6">
          <vs-button
            @click="toggleCart(product.id)"
            :color="isInCart(product.id) ? 'orange' : '#f3f4f6' /* gray-100 */"
            :text-color="isInCart(product.id) ? 'white' : '#374151' /* gray-700 */"
            type="filled"
            block
            :class="['w-full', 'font-semibold', { 'add-to-cart-btn': !isInCart(product.id) }]"
          >
            <span v-if="!isInCart(product.id)">{{ product.actionText }}</span>
            <span v-else>✔ Added to Cart</span>
          </vs-button>
        </div>
      </div>
    </div>

    <!-- Cart Summary and Checkout Section -->
    <div v-if="cartItems.length > 0" class="max-w-7xl mx-auto mt-12 bg-white p-6 rounded-2xl shadow-lg border">
        <h2 class="text-2xl font-bold text-gray-800 border-b pb-4 mb-4">Your Cart Summary</h2>
        <div v-for="item in cartItems" :key="item.id" class="flex justify-between items-center mb-2 text-gray-700">
            <span>{{ item.name }} ({{ item.userCount }} users)</span>
            <span class="font-medium">€{{ item.totalPrice.toFixed(2) }}</span>
        </div>
        <div class="border-t mt-4 pt-4 flex justify-between items-center font-bold text-xl text-gray-900">
            <span>Total Monthly Cost:</span>
            <span>€{{ totalPrice.toFixed(2) }}</span>
        </div>
        <vs-button
            @click="proceedToCheckout"
            :loading="isLoading"
            :disabled="isLoading"
            color="success"
            size="large"
            block
            class="mt-6 font-bold text-lg"
        >
            Proceed to Checkout
        </vs-button>
        <p v-if="errorMessage" class="text-center text-red-500 mt-4 font-medium">{{ errorMessage }}</p>
    </div>
  </div>
</template>

<script>
// The entire <script> block is unchanged and remains correct.
export default {
  name: "SubscriptionUpgrade",
  props: {
    orgData: { type: Object, required: true },
    userData: { type: Object, required: true }
  },
  data() {
    return {
      isLoading: false,
      errorMessage: null,
      apiBaseUrl: 'https://api-core.getboarded.com/api',
      cart: {},
      products: [
        {
          id: 'fitment',
          name: 'Fitment Plan',
          subtitle: 'Onboarding people to their roles.',
          icon: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />`,
          description: 'Mapping right talent to right roles with fitment indicators.',
          pricePerUser: 5,
          features: ['Road to your Role (Journey)', 'Skill profiling', 'Learning credits', 'Market rewards'],
          actionText: 'Add to Cart'
        },
        {
          id: 'development',
          name: 'Development Plan',
          subtitle: 'AI-Powered Learning with ALEX.',
          icon: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />`,
          description: 'Creating Learning plans with ALEX Personalized learning paths to increase talent value with skills.',
          pricePerUser: 10,
          features: ['Custom learning paths', 'AI assisted validation', 'Progress tracking with Experience Points (XPs)', 'Interactive exercises'],
          actionText: 'Add to Cart'
        },
        {
          id: 'succession',
          name: 'Succession Plan',
          subtitle: 'Promote and support career growth.',
          icon: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />`,
          description: 'Coaching plans for skill and time readiness to the next role.',
          pricePerUser: 15,
          features: ['360 Appraisal indicators', 'Goal Setting & Mentorship', 'Behavioural Coaching & Plans', 'Managed Career Guidance'],
          actionText: 'Add to Cart'
        }
      ]
    };
  },
  computed: {
    cartItems() {
        return Object.keys(this.cart).map(productId => {
            const product = this.products.find(p => p.id === productId);
            const userCount = this.cart[productId].userCount;
            return { ...product, userCount, totalPrice: product.pricePerUser * userCount };
        });
    },
    totalPrice() {
        return this.cartItems.reduce((total, item) => total + item.totalPrice, 0);
    }
  },
  methods: {
    isInCart(productId) {
      return !!this.cart[productId];
    },
    toggleCart(productId) {
      if (this.isInCart(productId)) {
        this.$delete(this.cart, productId);
      } else {
        this.$set(this.cart, productId, { userCount: 10 }); // Start at 10 users now
      }
    },
    async proceedToCheckout() {
      this.isLoading = true;
      this.errorMessage = null;
      try {
        const authToken = this.getAuthToken();
        const payload = {
          items: this.cartItems.map(item => ({ planId: item.id, quantity: item.userCount })),
          billingCycle: "monthly",
          organizationId: this.orgData._id,
          userId: this.userData._id,
          successUrl: `http://localhost:8080/organization/${this.orgData.slug}/dashboard`,
          cancelUrl: `http://localhost:8080/organization/${this.orgData.slug}/settings`,
        };
        if (payload.items.length === 0) throw new Error("Your cart is empty.");
        const response = await fetch(`${this.apiBaseUrl}/subscriptions/create-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
          body: JSON.stringify(payload)
        });
        const responseData = await response.json();
        if (!response.ok) throw new Error(responseData.error || 'Failed to create checkout session.');
        window.location.href = responseData.checkoutUrl;
      } catch (error) {
        console.error("Checkout failed:", error);
        this.errorMessage = error.message;
      } finally {
        this.isLoading = false;
      }
    },
    getAuthToken() {
      const apiToken = localStorage.getItem('accessToken');
      if (!apiToken) throw new Error('Authentication token not found. Please log in again.');
      return apiToken;
    },
  }
};
</script>

<style scoped>

.add-to-cart-btn:hover {
  background-color: #edad0ad5 !important; /* gray-800 (dark) */
}

.add-to-cart-btn:hover :deep(.vs-button__content) {
  color: #ffffff !important; /* white text */
}

.slider-custom {
  -webkit-appearance: none;
  appearance: none; 
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: #fed7aa;
  outline: none;
  opacity: 0.9;
  transition: opacity .2s;
}

.slider-custom:hover {
  opacity: 1;
}

.slider-custom::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 22px;
  height: 22px;
  border-radius: 50%; 
  background: #f97316;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 0 2px rgba(0,0,0,0.2);
}

.slider-custom::-moz-range-thumb {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f97316;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 0 2px rgba(0,0,0,0.2);
}
</style>
